import type { PublishDevice } from '@/components/publish-flow/types';
import type { ChannelVerdict } from './types';

const FEATURE_BLOCK_PHRASES: Record<string, string> = {
  geospatial: 'story has a map anchor',
  immersal: 'story has an Immersal scan',
  pinch_trigger: 'story has a pinch trigger',
  complex_stock_animation: 'story has complex animations',
};

export function featureBlockPhrase(feature: string): string {
  return FEATURE_BLOCK_PHRASES[feature] ?? `story uses ${feature}`;
}

function reasonPhrase(v: ChannelVerdict): string {
  const r = v.reasons[0];
  if (!r) return 'is blocked';
  if (r.type === 'feature_blocked' && r.feature) {
    return featureBlockPhrase(r.feature);
  }
  if (r.type === 'size_hard' && r.thresholdMB != null) {
    return `story is over ${r.thresholdMB} MB`;
  }
  return 'is blocked';
}

function formatReason(v: ChannelVerdict): string {
  return `Blocked because ${reasonPhrase(v)}`;
}

function formatPartial(
  v: ChannelVerdict,
  platform: 'iOS' | 'Android',
  appWorks = false,
): string {
  const phrase = reasonPhrase(v);
  const suffix = appWorks ? ' without installing the app' : '';
  return `This ${phrase}. It doesn't work on ${platform}${suffix}.`;
}

export interface DeviceBlockState {
  fullBlocks: Map<PublishDevice, string>;
  partialBlocks: Map<PublishDevice, string>;
}

export function deviceBlockStateFromVerdicts(
  verdicts: ChannelVerdict[] | undefined,
): DeviceBlockState {
  const fullBlocks = new Map<PublishDevice, string>();
  const partialBlocks = new Map<PublishDevice, string>();
  if (!verdicts) return { fullBlocks, partialBlocks };
  const find = (key: string) => verdicts.find((v) => v.key === key);

  const native_ios = find('native_ios');
  const native_android = find('native_android');
  const iosNativeBlocked = native_ios?.status === 'no';
  const androidNativeBlocked = native_android?.status === 'no';
  if (iosNativeBlocked && androidNativeBlocked) {
    fullBlocks.set('app', formatReason(native_ios!));
  } else if (iosNativeBlocked) {
    partialBlocks.set('app', formatPartial(native_ios!, 'iOS'));
  } else if (androidNativeBlocked) {
    partialBlocks.set('app', formatPartial(native_android!, 'Android'));
  }

  const appless_ios = find('appless_ios');
  const appless_android = find('appless_android');
  const iosApplessBlocked = appless_ios?.status === 'no';
  const androidApplessBlocked = appless_android?.status === 'no';
  if (iosApplessBlocked && androidApplessBlocked) {
    fullBlocks.set('appless', formatReason(appless_ios!));
  } else if (iosApplessBlocked) {
    partialBlocks.set(
      'appless',
      formatPartial(appless_ios!, 'iOS', native_ios != null && !iosNativeBlocked),
    );
  } else if (androidApplessBlocked) {
    partialBlocks.set(
      'appless',
      formatPartial(appless_android!, 'Android', native_android != null && !androidNativeBlocked),
    );
  }

  const specs = find('spectacles');
  if (specs?.status === 'no') fullBlocks.set('spectacles', formatReason(specs));

  return { fullBlocks, partialBlocks };
}

export function deviceBlockReasonsFromVerdicts(
  verdicts: ChannelVerdict[] | undefined,
): Map<PublishDevice, string> {
  return deviceBlockStateFromVerdicts(verdicts).fullBlocks;
}
